/**
 * The shell side of parser isolation (§30, §33).
 *
 * This wraps the real registry so that, for the Worker-safe formats, `probe`
 * and `open` run in a Worker instead of on the UI thread — and it does so
 * without `openFile` knowing anything changed, because it returns handlers with
 * the same shape. Three properties make it safe to switch on by default:
 *
 * - **It never makes the product worse than the inline path.** Any Worker
 *   failure — creation refused, crash, or a watchdog timeout on a file that
 *   hangs a parser — falls back to parsing on the main thread. The user still
 *   gets their file; they just lose the isolation for that one open.
 * - **A file's own errors are preserved.** A `handler` failure (damaged,
 *   encrypted, unsupported) is rethrown as the same `HandlerError` it would
 *   have been inline, so §34 messages are unchanged.
 * - **Cancellation is honoured.** When the caller aborts — the user opened
 *   something else — the pending parse rejects at once and its late result is
 *   ignored; a parse that is still running in the Worker is left to be
 *   reclaimed when the next open terminates it.
 */
import { HandlerError, WORKER_SAFE_FORMATS } from '@moduviewer/handlers';
import type { FileSource, FormatHandler, HandlerContext, HandlerRegistry } from '@moduviewer/handlers';
import type { ParseOp, ParseRequest, ParseResponse } from './parse-protocol.js';

/**
 * A parser that hangs is a denial of service (§30). This is the ceiling on how
 * long one probe or open may run in the Worker before it is terminated and the
 * open falls back to the main thread.
 */
const WATCHDOG_MS = 20_000;

interface Pending {
  readonly resolve: (value: unknown) => void;
  readonly reject: (reason: unknown) => void;
  readonly onAbort: () => void;
  readonly signal: AbortSignal | undefined;
  timer: ReturnType<typeof setTimeout>;
}

class WorkerParser {
  #worker: Worker | null = null;
  #nextId = 1;
  #pending = new Map<number, Pending>();
  #broken = false;

  constructor(private readonly makeWorker: () => Worker) {}

  /** Run one op in the Worker. Rejects with `{kind}` so the caller can route. */
  run(op: ParseOp, formatId: string, source: FileSource, ctx: HandlerContext): Promise<unknown> {
    const worker = this.#ensure();
    if (!worker) return Promise.reject(infra('worker unavailable'));
    if (ctx.signal.aborted) return Promise.reject(cancelled());

    const reqId = this.#nextId++;
    return new Promise<unknown>((resolve, reject) => {
      const settle = (fn: (v: never) => void, v: unknown): void => {
        const pending = this.#pending.get(reqId);
        if (!pending) return;
        clearTimeout(pending.timer);
        if (pending.signal) pending.signal.removeEventListener('abort', pending.onAbort);
        this.#pending.delete(reqId);
        (fn as (x: unknown) => void)(v);
      };

      const onAbort = (): void => settle(reject as never, cancelled());
      const timer = setTimeout(() => {
        // The file hung the parser. Kill the Worker so the runaway stops, and
        // report infra so the caller retries inline.
        this.#recycle();
        settle(reject as never, infra('worker timed out'));
      }, WATCHDOG_MS);

      this.#pending.set(reqId, { resolve: resolve as never, reject: reject as never, onAbort, signal: ctx.signal, timer });
      if (ctx.signal) ctx.signal.addEventListener('abort', onAbort, { once: true });

      const message: ParseRequest = {
        reqId, op, formatId, source, limits: ctx.limits, assessment: ctx.assessment,
      };
      try {
        worker.postMessage(message);
      } catch (err) {
        // A structured-clone failure means this model cannot cross the boundary
        // — treat it as infra so the caller parses inline.
        settle(reject as never, infra(`postMessage failed: ${(err as Error).message}`));
      }
    });
  }

  #ensure(): Worker | null {
    if (this.#broken) return null;
    if (this.#worker) return this.#worker;
    try {
      const worker = this.makeWorker();
      worker.onmessage = (event: MessageEvent<ParseResponse>) => this.#onMessage(event.data);
      worker.onerror = () => this.#recycle();
      this.#worker = worker;
      return worker;
    } catch {
      // Worker construction is unavailable (an old runtime, a restrictive CSP).
      // Give up on isolation for the session rather than retry every open.
      this.#broken = true;
      return null;
    }
  }

  #onMessage(response: ParseResponse): void {
    const pending = this.#pending.get(response.reqId);
    if (!pending) return; // an abandoned (aborted or timed-out) request
    clearTimeout(pending.timer);
    if (pending.signal) pending.signal.removeEventListener('abort', pending.onAbort);
    this.#pending.delete(response.reqId);
    if (response.ok) pending.resolve(response.result);
    else pending.reject({ kind: response.kind, messageKey: response.messageKey, message: response.message });
  }

  /** Kill the current Worker and fail every request in flight as infra. */
  #recycle(): void {
    const worker = this.#worker;
    this.#worker = null;
    if (worker) { worker.onmessage = null; worker.onerror = null; worker.terminate(); }
    for (const [reqId, pending] of this.#pending) {
      clearTimeout(pending.timer);
      if (pending.signal) pending.signal.removeEventListener('abort', pending.onAbort);
      this.#pending.delete(reqId);
      pending.reject(infra('worker recycled'));
    }
  }
}

interface RoutedError { readonly kind: 'handler' | 'infra'; readonly messageKey: string; readonly message: string }

function infra(message: string): RoutedError { return { kind: 'infra', messageKey: 'error.unableToRead', message }; }
function cancelled(): RoutedError { return { kind: 'handler', messageKey: 'action.cancel', message: 'parsing was cancelled' }; }
function isRouted(value: unknown): value is RoutedError {
  return typeof value === 'object' && value !== null && 'kind' in value;
}

/**
 * Wrap a registry so that Worker-safe formats parse off the main thread.
 *
 * The returned object exposes exactly what {@link openFile} uses — `get` and
 * `canEditFormat` — plus `has`/`audit` for completeness. Formats outside the
 * safe set are served by the original registry unchanged.
 */
export function createIsolatedRegistry(
  main: HandlerRegistry, makeWorker: () => Worker,
): HandlerRegistry {
  const parser = new WorkerParser(makeWorker);

  const proxyFor = (formatId: string, handler: FormatHandler<unknown>): FormatHandler<unknown> => {
    const viaWorker = (op: ParseOp) => async (source: FileSource, ctx: HandlerContext): Promise<never> => {
      try {
        return await parser.run(op, formatId, source, ctx) as never;
      } catch (routed) {
        if (isRouted(routed)) {
          if (routed.kind === 'handler') throw new HandlerError(routed.messageKey, routed.message);
          // infra — the Worker failed. Parse inline so the open still succeeds.
          if (ctx.signal.aborted) throw new HandlerError('action.cancel', 'parsing was cancelled');
          return (op === 'probe' ? handler.probe(source, ctx) : handler.open(source, ctx)) as never;
        }
        throw routed;
      }
    };
    return { ...handler, probe: viaWorker('probe'), open: viaWorker('open') };
  };

  return {
    get(formatId: string) {
      const handler = main.get(formatId);
      if (!handler) return null;
      if (!WORKER_SAFE_FORMATS.has(formatId)) return handler;
      return proxyFor(formatId, handler) as ReturnType<HandlerRegistry['get']>;
    },
    has(formatId: string) { return main.has(formatId); },
    canEditFormat(formatId: string) { return main.canEditFormat(formatId); },
    audit() { return main.audit(); },
  } as unknown as HandlerRegistry;
}
